/**
 * Where a visitor came from before signing up (first non-direct touch).
 * Captured client-side into a cookie by FirstTouchTracker, read on the server
 * when the new-user admin notification is sent, kept in auth user_metadata.
 */

export const SIGNUP_SOURCE_COOKIE = 'bastion_src';

export type SignupSource = {
  ref?: string;          // external referring domain, e.g. "x.com"
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing?: string;      // first page path, e.g. "/pulse"
  ts?: string;           // ISO time of the touch
};

const MAX_LEN = 120;

function clean(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const v = value.trim().replace(/[\u0000-\u001f<>"]/g, '').slice(0, MAX_LEN);
  return v || undefined;
}

export function sanitizeSignupSource(raw: unknown): SignupSource | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const src: SignupSource = {
    ref: clean(r.ref)?.toLowerCase(),
    utm_source: clean(r.utm_source),
    utm_medium: clean(r.utm_medium),
    utm_campaign: clean(r.utm_campaign),
    landing: clean(r.landing),
    ts: clean(r.ts),
  };
  for (const k of Object.keys(src) as (keyof SignupSource)[]) {
    if (src[k] === undefined) delete src[k];
  }
  return Object.keys(src).length > 0 ? src : null;
}

export function parseSignupSourceCookie(value: string | undefined | null): SignupSource | null {
  if (!value) return null;
  try {
    return sanitizeSignupSource(JSON.parse(decodeURIComponent(value)));
  } catch {
    return null;
  }
}

/** Browser-only: the source currently stored in the cookie. */
export function readSignupSourceCookie(): SignupSource | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${SIGNUP_SOURCE_COOKIE}=([^;]*)`));
  return parseSignupSourceCookie(match?.[1]);
}

export function isDirectSource(src: SignupSource | null | undefined): boolean {
  return !src?.ref && !src?.utm_source && !src?.utm_campaign;
}

/** Short label for the email subject: "x.com", "newsletter", "Direct". */
export function signupSourceShort(src: SignupSource | null | undefined): string {
  if (!src) return 'unknown';
  return src.utm_source || src.ref || 'Direct';
}

/** One-line label for the notification body. */
export function formatSignupSource(src: SignupSource | null | undefined): string {
  if (!src) return 'Unknown';
  const parts: string[] = [src.ref || (src.utm_source ? '' : 'Direct')].filter(Boolean);
  const utm = [src.utm_source, src.utm_medium, src.utm_campaign].filter(Boolean).join(' / ');
  if (utm) parts.push(`utm: ${utm}`);
  if (src.landing) parts.push(`landed on ${src.landing}`);
  return parts.join(' · ');
}
